export class PreloadCache {
  #cachedStreamDataMap = new Map();

  #streamDataDestroyMap = new Map();

  #maxPreloadStreams = 0;

  constructor(maxPreloadStreams) {
    this.#maxPreloadStreams = maxPreloadStreams;
  }

  #destroyCachedStreamData = (streamData, guideId) => {
    try {
      streamData?.players?.forEach((player) => player.destroy());
    } catch {
      // null
    }

    this.#cachedStreamDataMap.delete(guideId);
  };

  get(guideId) {
    return (
      this.#cachedStreamDataMap.get(guideId) ||
      this.#streamDataDestroyMap.get(guideId)
    );
  }

  set(guideId, streamData = null) {
    this.#cachedStreamDataMap.set(guideId, streamData);
  }

  delete(guideId) {
    const streamData = this.#cachedStreamDataMap.get(guideId);

    if (streamData) {
      this.#destroyCachedStreamData(streamData, guideId);
    }
  }

  canPreloadMoreStreams(guideId) {
    return (
      this.#cachedStreamDataMap.size < this.#maxPreloadStreams &&
      !this.#cachedStreamDataMap.has(guideId)
    );
  }

  isScheduledForDestroy(guideId) {
    return !!this.#streamDataDestroyMap.get(guideId);
  }

  restoreFromDestroyQueue(guideId) {
    this.set(guideId, this.#streamDataDestroyMap.get(guideId));
    this.#streamDataDestroyMap.delete(guideId);
  }

  processDestroyQueue() {
    if (!this.#streamDataDestroyMap.size) {
      return;
    }

    const destroyMap = this.#streamDataDestroyMap;
    this.#streamDataDestroyMap = new Map();

    destroyMap.forEach((streamData) => {
      try {
        streamData?.players?.forEach((player) => player.destroy());
      } catch (error) {
        // noop
      }
    });
  }

  pluckCachedStreamData(guideId) {
    const streamData = this.get(guideId);

    if (streamData) {
      this.#cachedStreamDataMap.delete(guideId);
    }

    // No cached stream data means this is a tune of a non-preloaded stream, so at least 1 preloaded
    // stream must be deleted to guarantee the browser has enough resources to play the new stream.
    if (!streamData && this.#maxPreloadStreams) {
      try {
        const entries = this.#cachedStreamDataMap.entries();

        const [nextGuideId] = entries.next().value;

        this.delete(nextGuideId);
      } catch (error) {
        // noop
      }
    }

    return streamData;
  }

  destroyAllCachedStreamData() {
    this.#cachedStreamDataMap.forEach(this.#destroyCachedStreamData);
  }

  scheduleDestroyPreloads(currentlyPlayingGuideId) {
    this.#streamDataDestroyMap.forEach(this.#destroyCachedStreamData);
    this.#streamDataDestroyMap = this.#cachedStreamDataMap;
    this.#cachedStreamDataMap = new Map();

    if (currentlyPlayingGuideId) {
      this.restoreFromDestroyQueue(currentlyPlayingGuideId);
    }
  }
}
